import { useRouter } from 'next/navigation';
import React from 'react';
import { POINT_CHARGE_BASE_PATH } from '@/domain/pointChargeV2/constants/pointCharge3ds';
import { CurrentUnextPointBanner } from '@/shared/components/composites/CurrentUnextPointBanner';
import { PortalModal } from '@/shared/components/composites/PortalModal';
import { Spacer } from '@/shared/components/parts/Spacer';
import { TextsForSCTA } from '@/shared/components/parts/TextsForSCTA';
import { ButtonPrimary } from '@/shared/components/parts/buttons/ButtonPrimary';
import type { Shared_CurrentBillingMethodQuery } from '@/shared/gql/__codegen__/graphql';
import { insertCommaDelimiter } from '@/shared/helpers/formatter/insertCommaDelimiter';
import { useCurrentBillingMethodQuery } from '@/shared/hooks/useCurrentBillingMethodQuery';
import { useUserInfoAndMenus } from '@/shared/hooks/useUserInfoAndMenus';
import type { UsePointChargeTop } from '../index.hook';

function resolveIsCareerPayment(
  data: Shared_CurrentBillingMethodQuery | undefined
) {
  if (!data) return false;

  switch (data.unextAccount.currentBillingMethod?.__typename) {
    case 'BasicPaymentInfo': {
      // NSBO キャリア
      return ['40', '45'].includes(data.unextAccount.currentBillingMethod.id);
    }

    // BOS SBPS キャリア
    case 'SbpsV2PaymentInfo': {
      return true;
    }

    default: {
      return false;
    }
  }
}

export const ConfirmForm: React.FC<{
  state: UsePointChargeTop['state'];
}> = ({ state }) => {
  const router = useRouter();
  const [isShownModal, setIsShownModal] = React.useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = React.useState<string>('');

  const { userInfo } = useUserInfoAndMenus();
  const { data } = useCurrentBillingMethodQuery({
    skip: userInfo.isYamadaPF,
  });

  return (
    <>
      <div className="cst-side-space mx-auto max-w-180">
        {resolveIsCareerPayment(data) && (
          <>
            <div className="rounded-2 bg-main-80 p-4 text-base-100">
              <div className="text-h4 font-bold cst-halt leading-[1.4]">
                お支払い方法を変えるだけで、もっとお得に
              </div>
              <Spacer spacing={2} />
              <div className="text-base2">
                キャリア決済でご利用中のお客様へ。
                <br />
                月額プランのお支払い方法をクレジットカードにご変更いただくと、
                <span className="font-bold">2,000ポイントプレゼント</span>
                キャンペーンを実施中です。 詳しくは
                <a
                  href="https://www.video.unext.jp/lp/migrate_to_credit_card"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  こちら
                </a>
                から。
              </div>
            </div>
            <Spacer spacing={12} />
          </>
        )}

        <div className="rounded-3 border border-solid border-main-10 bg-base-100">
          <div>
            <div className="flex justify-center border-b border-main-10 p-4">
              <CurrentUnextPointBanner />
            </div>
            <div className="px-4 py-6">
              <div className="text-main-100">
                必要なポイントをチャージしてください。
                <br />
                チャージしたU-NEXTポイントの有効期限は180日間です。
              </div>
              <Spacer spacing={6} />
              <div className="grid grid-cols-1 gap-2">
                {state.products.map(({ productId, salePrice, pointAmount }) => (
                  <div key={productId} className="rounded-2 bg-main-5 p-4">
                    <div className="grid grid-cols-[1fr_11rem] items-center gap-2">
                      <div className="flex flex-wrap items-baseline">
                        <div className="text-base1 font-bold cst-halt leading-[1.4] text-main-100">
                          {insertCommaDelimiter(pointAmount)}
                        </div>
                        <div className="text-base2">ポイント</div>
                      </div>
                      <ButtonPrimary
                        text={`${insertCommaDelimiter(salePrice)}円で購入`}
                        onClick={() => {
                          setSelectedProductId(productId);
                          setIsShownModal(true);
                        }}
                        prefixIcon="purchase"
                        width="full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Spacer spacing={8} />
        <div className="text-base3 text-main-40">
          ※1ポイントは1円として利用できます。
          <br />
          <TextsForSCTA />
        </div>
      </div>
      {isShownModal && (
        <PortalModal
          title="確認"
          content={
            <div>
              {insertCommaDelimiter(
                state.products.find(
                  ({ productId }) => productId === selectedProductId
                )?.salePrice ?? 0
              )}
              円で ポイント購入しますか？
            </div>
          }
          primaryButton={{
            type: 'submit',
            text: '購入',
            onClick: () => {
              router.push(
                `${POINT_CHARGE_BASE_PATH}/dispatch/${selectedProductId}`
              );
            },
          }}
          secondaryButton={{
            type: 'cancel',
            text: 'キャンセル',
            onClick: () => {
              setIsShownModal(false);
            },
          }}
        />
      )}
    </>
  );
};
